import { Injectable, ViewContainerRef, ComponentRef } from '@angular/core';
import { NavService } from './nav.service';

/**
 * Service for dynamically loading Angular components on-demand.
 *
 * This service provides lazy-loading functionality for heavy UI components
 * (Calculator, Graph, PeriodicTable) that are not needed at initial app load.
 *
 * Features:
 * - Dynamic component creation using ViewContainerRef
 * - Proper lifecycle management (create, track, destroy)
 * - Prevents duplicate component instances
 * - Automatically subscribes to component close events
 * - Syncs with NavService state to ensure proper open/close toggle behavior
 * - Reduces initial bundle size by code-splitting components
 *
 * @example
 * ```typescript
 * await this.lazyLoader.loadCalculator(this.viewContainerRef);
 * // User closes calculator
 * this.lazyLoader.destroyCalculator();
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class LazyComponentLoaderService {
  private calculatorRef?: ComponentRef<any>;
  private graphRef?: ComponentRef<any>;
  private periodicTableRef?: ComponentRef<any>;

  constructor(private navService: NavService) {}

  /**
   * Dynamically loads and creates the Calculator component.
   * Creates only one instance at a time.
   *
   * @param container ViewContainerRef where the component will be created
   */
  async loadCalculator(container: ViewContainerRef): Promise<void> {
    if (this.calculatorRef) {
      return; // Already loaded
    }

    const { CalculatorComponent } = await import('../common/calculator/calculator.component');
    this.calculatorRef = container.createComponent(CalculatorComponent);

    // Subscribe to close event - update NavService to sync state
    this.calculatorRef.instance.closeCalculator.subscribe(() => {
      this.destroyCalculator();
      this.navService.closeCalculator(); // Update NavService state
    });
  }

  /**
   * Destroys the Calculator component and cleans up resources.
   */
  destroyCalculator(): void {
    if (this.calculatorRef) {
      this.calculatorRef.destroy();
      this.calculatorRef = undefined;
    }
  }

  /**
   * Dynamically loads and creates the Graph component.
   * Creates only one instance at a time.
   *
   * @param container ViewContainerRef where the component will be created
   */
  async loadGraph(container: ViewContainerRef): Promise<void> {
    if (this.graphRef) {
      return; // Already loaded
    }

    const { GraphComponent } = await import('../common/graph/graph.component');
    this.graphRef = container.createComponent(GraphComponent);

    // Subscribe to close event - update NavService to sync state
    this.graphRef.instance.closed.subscribe(() => {
      this.destroyGraph();
      this.navService.closeGraph(); // Update NavService state
    });
  }

  /**
   * Destroys the Graph component and cleans up resources.
   */
  destroyGraph(): void {
    if (this.graphRef) {
      this.graphRef.destroy();
      this.graphRef = undefined;
    }
  }

  /**
   * Dynamically loads and creates the PeriodicTable component.
   * Creates only one instance at a time.
   *
   * @param container ViewContainerRef where the component will be created
   */
  async loadPeriodicTable(container: ViewContainerRef): Promise<void> {
    if (this.periodicTableRef) {
      return; // Already loaded
    }

    const { PeriodicTableComponent } = await import(
      '../common/periodic-table/periodic-table.component'
    );
    this.periodicTableRef = container.createComponent(PeriodicTableComponent);

    // Subscribe to close event - update NavService to sync state
    this.periodicTableRef.instance.closed.subscribe(() => {
      this.destroyPeriodicTable();
      this.navService.closePeriodicTable(); // Update NavService state
    });
  }

  /**
   * Destroys the PeriodicTable component and cleans up resources.
   */
  destroyPeriodicTable(): void {
    if (this.periodicTableRef) {
      this.periodicTableRef.destroy();
      this.periodicTableRef = undefined;
    }
  }
}

